import moment from 'moment';
import PropTypes from 'prop-types';
import React, { memo, Fragment } from 'react';
import styled from 'styled-components';

import useAPI from '@hooks/useAPI';
import Button from '@shared/Button';
import Spinner from '@shared/Spinner';
import TooltipWrapper from '@shared/TooltipWrapper';

import HitList from './HitList';
import LocalStoreAlert from './LocalStoreAlert';

const EscalatedBtn = styled(Button)`
  background-color: ${props => props.theme.invalid};
  border-radius: 5px;
  color: ${props => props.theme.enigmaWhite};
  font-size: ${props => props.theme.fontLg};
  height: 36px;
  margin: 0px;
  width: 120px;
  div {
    padding: 10px;
    margin: 0 auto;
  }
`;

const AlertIdContainer = styled.div`
  color: ${props => props.theme.enigmaBlack};
  display: inline-block;
  font-family: 'GT Mono', monospace;
  font-size: 20px;
  font-weight: 700;
  margin: 10px 0px;
`;
const NoResultsContainer = styled.div`
  margin: 40px auto;
  text-align: center;
`;

const TimeAgoSnippet = styled.div`
  color: ${props => props.theme.enigmaDark};
  display: inline-block;
  font-size: 16px;
  font-weight: 300;
  line-height: 24px;
  margin: 5px 10px;
`;

const RootContainer = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  padding: 24px;
  width: 100%;
`;

const ValidContainer = styled(RootContainer)`
  background-color: ${props => props.theme.validBg};
`;

const InvalidContainer = styled(RootContainer)`
  background-color: ${props => props.theme.invalidBg};
`;

const RowContainer = styled.div`
  width: 100%;
  display: inline-block;
  align-items: center;
  .item {
    display: inline-block;
  }
`;

const VersionButton = styled.div`
  display: inline-block;
  padding: 5px;
  margin: 0px;
  font-weight: 300;
  color: ${props => props.theme.fontTertiary};
  background: ${props => props.theme.enigmaWhite};
  border: ${props => props.theme.borderMd};
  border-radius: 5px;
`;

const FloatRightContainer = styled.div`
  float: right;
`;
const ItemContainer = styled.div`
  display: inline-block;
  margin: 0px 15px 0 0;
  font-weight: 300;
`;

const ClearedBtn = styled(EscalatedBtn)`
  width: 120px;
  background-color: ${props => props.theme.valid};
  color: ${props => props.theme.enigmaWhite};
`;

function DecisionSummary({ id }) {
  const res = useAPI(
    'get',
    'ScreenConsoleListApi',
    `alerts/${id}`,
    '',
    false,
    '',
    false
  );

  let { response, isLoading } = res;

  if (isLoading) {
    return (
      <div>
        <Spinner center />
      </div>
    );
  }


  const { timestamp, modelVersion = '0.1', decision, decision_id, hits = [] } =
    response || {};

  if (!decision_id) {
    return <NoResultsContainer>No results found.</NoResultsContainer>;
  }

  const timeAgo = moment(timestamp).fromNow();
  const timeExact = moment(timestamp).format('LLLL');
  const cleared = hits.filter(item => item.decision === 0);
  let clearedPct;
  if (!hits.length) {
    clearedPct = 0;
  } else {
    clearedPct = Math.floor((cleared.length / hits.length) * 100);
  }
  const StatusContainer = decision ? InvalidContainer : ValidContainer;

  return (
    <Fragment>
      <LocalStoreAlert decision={decision} id={id} />
      <StatusContainer>
        <RowContainer>
          <TooltipWrapper tooltip='Alert Decision' placement='right'>
            {decision ? (
              <EscalatedBtn>Escalated</EscalatedBtn>
            ) : (
              <ClearedBtn>Cleared</ClearedBtn>
            )}
          </TooltipWrapper>
          <FloatRightContainer>
            <VersionButton>Model V {modelVersion}</VersionButton>
          </FloatRightContainer>
          &nbsp;
          <FloatRightContainer>
            <TooltipWrapper tooltip={timeExact} placement='bottom'>
              <TimeAgoSnippet>{timeAgo}</TimeAgoSnippet>
            </TooltipWrapper>
          </FloatRightContainer>
          &nbsp;
        </RowContainer>
        <RowContainer>
          <TooltipWrapper tooltip='Alert ID#' placement='bottom'>
            <AlertIdContainer>{id}</AlertIdContainer>
          </TooltipWrapper>
          &nbsp;
        </RowContainer>
        <RowContainer>
          <ItemContainer>{hits.length} total hits</ItemContainer>
          <ItemContainer>{clearedPct}% of hits cleared</ItemContainer>
        </RowContainer>
      </StatusContainer>
      <RowContainer>
        <HitList hits={hits} alertId={id} />
      </RowContainer>
    </Fragment>
  );
}

DecisionSummary.propTypes = {
  id: PropTypes.string.isRequired,
  timestamp: PropTypes.string.isRequired,
};

export default memo(DecisionSummary);
